import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as TextTypes } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { ContentItemViewNodesType } from '../../../types'
import { sortNodes } from './sortingUtils'

/**
 * Finds a node by its element ID
 * @param nodes - Array of nodes to search through
 * @param elementId - The element ID to search for
 * @returns The found node or undefined
 */
export const findNodeById = (
    nodes: Partial<ContentItemViewNodesType>[],
    elementId: string
): Partial<ContentItemViewNodesType> | undefined => {
    return nodes.find((node) =>
        node.elements?.some((element) => element.id === elementId)
    )
}

/**
 * Finds the parent node of a given child node
 * @param nodes - Array of all nodes
 * @param childNode - The child node to find parent for
 * @returns The parent node or undefined
 */
export const findParentNode = (
    nodes: Partial<ContentItemViewNodesType>[],
    childNode: Partial<ContentItemViewNodesType>
): Partial<ContentItemViewNodesType> | undefined => {
    return nodes.find((node) => {
        if (childNode.dtvContentItemParentId) {
            return node.id === childNode.dtvContentItemParentId
        }
        return node.dtvItemId === childNode.dtvParentId
    })
}

/**
 * Finds all siblings of a node (nodes with the same parent)
 * @param nodes - Array of all nodes
 * @param parentNode - The parent node (undefined for top-level items)
 * @param excludeId - Optional ID to exclude from siblings
 * @returns Array of sibling nodes
 */
export const findSiblings = (
    nodes: Partial<ContentItemViewNodesType>[],
    parentNode: Partial<ContentItemViewNodesType> | undefined,
    excludeId?: string
): Partial<ContentItemViewNodesType>[] => {
    const result = nodes.filter((node) => {
        // excluding current node and non-paragraph types
        if (excludeId && node.id === excludeId) return false
        if (node.textType !== TextTypes.Paragraph) return false

        // determine parent node relationships using TreeDataMapper logic
        if (parentNode) {
            // Match TreeDataMapper.findChildren() logic exactly
            const isChildNode =
                node?.refBodItemId === '' &&
                node.dtvContentItemParentId === parentNode.id
            const isDirectChildNode =
                node.dtvParentId === parentNode.dtvItemId &&
                !node.dtvContentItemParentId &&
                node?.refBodItemId === ''
            const isBodChildNode = node?.refBodItemId === parentNode.dtvItemId

            return isChildNode || isDirectChildNode || isBodChildNode
        }

        // top-level items (no parent)
        return !node.dtvParentId && !node.dtvContentItemParentId
    })

    // Sort by order to ensure consistent previous sibling detection
    return sortNodes(result, 'order')
}

/**
 * Finds a valid parent for demotion from the siblings
 * @param siblings - Array of sibling nodes
 * @param currentNode - The node being demoted
 * @returns A valid parent node or undefined
 */
export const findValidDemoteParent = (
    siblings: Partial<ContentItemViewNodesType>[],
    currentNode: Partial<ContentItemViewNodesType>
): Partial<ContentItemViewNodesType> | undefined => {
    // find previous siblings that can be parents (not BOD)
    const previousSiblings = siblings.filter(
        (s) =>
            s.order !== undefined &&
            currentNode.order !== undefined &&
            s.order < currentNode.order
    )

    // Only consider previous siblings for demotion
    if (previousSiblings.length <= 0) {
        return undefined // No valid parent found
    }

    // Sort previous siblings by order (descending) to get the closest previous sibling
    const sortedPreviousSiblings = sortNodes(previousSiblings, 'order', false)

    return sortedPreviousSiblings.find((s) => !s.isBOD)
}
